// ===================================================
// ФАЙЛ: frontend/src/services/companies.service.js
// Сервис для работы с API управления компаниями
// ===================================================

import axios from '../utils/axios';

const BASE_PATH = '/api/admin/companies';

const companiesService = {
  /**
   * Получить список всех компаний (только для администраторов)
   */
  getCompanies: async (params = {}) => {
    const { data } = await axios.get(BASE_PATH, { params });
    // API возвращает { success, data: { companies, pagination } }
    return data.data || data;
  },

  /**
   * Алиас для обратной совместимости
   */
  getAllCompanies: async (params = {}) => {
    return companiesService.getCompanies(params);
  },

  /**
   * Получить детали компании по ID
   */
  getCompanyById: async (companyId) => {
    const { data } = await axios.get(`${BASE_PATH}/${companyId}`);
    return data.data || data;
  },

  /**
   * Получить статистику компании (использование ресурсов, файлы и т.д.)
   */
  getCompanyStats: async (companyId) => {
    const { data } = await axios.get(`${BASE_PATH}/${companyId}/stats`);
    return data.data || data;
  },

  /**
   * Получить действия компании (логи активности)
   */
  getCompanyActions: async (companyId, params = {}) => {
    const { data } = await axios.get(`${BASE_PATH}/${companyId}/actions`, { params });
    return data.data || data;
  },

  /**
   * Получить историю транзакций баланса
   */
  getBalanceHistory: async (companyId, params = {}) => {
    const { data } = await axios.get(`${BASE_PATH}/${companyId}/transactions`, { params });
    return data.data || data;
  },

  /**
   * Обновить данные компании
   */
  updateCompany: async (companyId, updates) => {
    const { data } = await axios.put(`${BASE_PATH}/${companyId}`, updates);
    return data;
  },

  /**
   * Ручная верификация компании (суперадмин)
   */
  verifyCompany: async (companyId) => {
    const { data } = await axios.post(`${BASE_PATH}/${companyId}/verify`, {});
    return data;
  },

  /**
   * Изменить баланс компании (универсальный метод)
   */
  updateBalance: async (companyId, amount, transactionType, description = '', category = '') => {
    const { data } = await axios.post(`${BASE_PATH}/${companyId}/balance`, {
      amount,
      transaction_type: transactionType,
      description,
      category
    });
    return data.data || data;
  },

  /**
   * Добавить средства на баланс компании
   */
  addBalance: async (companyId, amount, description = '', category = '') => {
    return companiesService.updateBalance(companyId, amount, 'deposit', description, category);
  },

  /**
   * Списать средства с баланса компании
   */
  deductBalance: async (companyId, amount, description = '', category = '') => {
    return companiesService.updateBalance(companyId, amount, 'withdrawal', description, category);
  },

  /**
   * Установить баланс компании (корректировка)
   */
  setBalance: async (companyId, amount, description = '', category = '') => {
    return companiesService.updateBalance(companyId, amount, 'adjustment', description, category);
  },

  /**
   * Удалить/деактивировать компанию
   */
  deleteCompany: async (companyId) => {
    const { data } = await axios.delete(`${BASE_PATH}/${companyId}`);
    return data;
  },

  /**
   * Снимок клиентской компании для переноса на свой сервер (не ИП/ООО).
   */
  exportTenant: async (companyId) => {
    const { data } = await axios.get(`/api/admin/client-tenants/${companyId}/export`);
    return data;
  },

  /**
   * Полное удаление клиентской компании и витрин. Нужны точное имя и фраза УДАЛИТЬ НАВСЕГДА.
   */
  purgeTenant: async (companyId, { confirm_name, confirm_phrase } = {}) => {
    const { data } = await axios.post(`/api/admin/client-tenants/${companyId}/purge`, {
      confirm_name,
      confirm_phrase,
    });
    return data;
  },

  /**
   * Активировать/деактивировать компанию (ЕДИНЫЙ унифицированный метод)
   * @param {string} companyId - ID компании
   * @param {boolean} isActive - true для активации, false для деактивации
   * @param {string} reason - причина деактивации (опционально)
   */
  toggleCompanyStatus: async (companyId, isActive, reason = '') => {
    const { data } = await axios.post(`${BASE_PATH}/${companyId}/toggle-status`, {
      is_active: isActive,
      reason: reason || undefined
    });
    return data;
  },

  /**
   * Получить сводку по всем компаниям
   */
  getCompaniesSummary: async () => {
    const { data } = await axios.get(`${BASE_PATH}/summary`);
    // API возвращает { success, data: { summary } }
    return data.data || data;
  },
};

export default companiesService;

